import { LocationVarKey } from "@swivl-mono/types";

import { IF_ANY, IS_EQUAL, IS_NOT_EQUAL, IS_TRUE } from "../../../Components/Worksheets/Types/Worksheet.Interface";
import {
  DropdownRow,
  Indent,
  InputRow,
  MultiLineRow,
  PageHeader,
  Section,
  SectionHeader,
  SwitchRow,
  TextAreaRow,
  TextRow
} from "../../../Components/Worksheets/WorksheetComponent/WorksheetComponents";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";

export const PAGE_KEYS: LocationVarKey[] = [
  LocationVarKey.FMSisLeadSentPersonaPotential,
  LocationVarKey.FMSisLeadSentPersonaExisting,
  LocationVarKey.FMSisLeadSentPersonaUnknown,
  LocationVarKey.FMSisLeadSentTagSales,
  LocationVarKey.FMSisLeadSentTagWaitlist,
  LocationVarKey.FMSisLeadSentTagContact,
  LocationVarKey.FMSisLeadSentTagService,
  LocationVarKey.FMSisLeadSentTagProblem,
  LocationVarKey.FMSisLeadSentTagEscalation,
  LocationVarKey.FMSisLeadSentTagUnknown,
  LocationVarKey.WantsEmailReceipts,
  LocationVarKey.swivlLeadEmailList,
  LocationVarKey.HasSegmentEmailReceiptLists,
  LocationVarKey.PotentialPersonaEmailList,
  LocationVarKey.ExistingPersonaEmailList,
  LocationVarKey.UnknownPersonaEmailList,
  LocationVarKey.SalesTagEmailList,
  LocationVarKey.WaitlistTagEmailList,
  LocationVarKey.ContactTagEmailList,
  LocationVarKey.ServiceTagEmailList,
  LocationVarKey.ProblemTagEmailList,
  LocationVarKey.EscalationTagEmailList,
  LocationVarKey.UnknownTagEmailList
];

export default function WorksheetView() {
  const page = usePropertiesGetLocationIds();
  return (
    <>
      <PageHeader
        {...page}
        subheading="Customers that have been identified as a persona below will be sent into your FMS as a lead"
        hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")}
      >
        {" "}
        Send Lead to FMS (Persona)
      </PageHeader>
      <SwitchRow
        {...page}
        hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")}
        keyName={LocationVarKey.FMSisLeadSentPersonaPotential}
        label="Potential"
      />
      <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")} keyName={LocationVarKey.FMSisLeadSentPersonaExisting} label="Existing" />
      <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")} keyName={LocationVarKey.FMSisLeadSentPersonaUnknown} label="Unknown" />

      <SectionHeader
        {...page}
        subheading="Customers that have been identified as a tag below will be sent into your FMS as a lead"
        hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")}
      >
        {" "}
        Send Lead to FMS (Tags)
      </SectionHeader>
      <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")} keyName={LocationVarKey.FMSisLeadSentTagSales} label="Sales" />
      <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")} keyName={LocationVarKey.FMSisLeadSentTagWaitlist} label="Waitlist" />
      <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")} keyName={LocationVarKey.FMSisLeadSentTagContact} label="Contact" />
      <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")} keyName={LocationVarKey.FMSisLeadSentTagService} label="Service" />
      <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")} keyName={LocationVarKey.FMSisLeadSentTagProblem} label="Problem" />
      <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")} keyName={LocationVarKey.FMSisLeadSentTagEscalation} label="Escalation" />
      <SwitchRow {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.HasFMS, "1")} keyName={LocationVarKey.FMSisLeadSentTagUnknown} label="Unknown" />

      <SectionHeader {...page}>Email Settings</SectionHeader>
      <SwitchRow {...page} keyName={LocationVarKey.WantsEmailReceipts} label="Send Email Receipts" />
      <Section {...page} hideIf={IS_NOT_EQUAL(LocationVarKey.WantsEmailReceipts, "1")}>
        <div className="cortexDescription">
          We will send an email containing all the information we've collected on a person, including actions taken with that information. By default,
          all emails go to the recipients in the <i>Email Recipients</i> field. If you want more control over who those emails are sent to, turn on{" "}
          <i>Segment Emails</i>.
        </div>
        <InputRow {...page} keyName={LocationVarKey.swivlLeadEmailList} label="Email recipients" />
        <SwitchRow {...page} keyName={LocationVarKey.HasSegmentEmailReceiptLists} label="Segment Emails" />
      </Section>
      <Section
        {...page}
        hideIf={IF_ANY([IS_NOT_EQUAL(LocationVarKey.HasSegmentEmailReceiptLists, "1"), IS_NOT_EQUAL(LocationVarKey.WantsEmailReceipts, "1")])}
      >
        <InputRow {...page} keyName={LocationVarKey.PotentialPersonaEmailList} label="Custom email recipients - Potential Persona" />
        <InputRow {...page} keyName={LocationVarKey.ExistingPersonaEmailList} label="Custom email recipients - Existing Persona" />
        <InputRow {...page} keyName={LocationVarKey.UnknownPersonaEmailList} label="Custom email recipients - Unknown Persona" />
        <InputRow {...page} keyName={LocationVarKey.SalesTagEmailList} label="Custom email recipients - Sales Tag" />
        <InputRow {...page} keyName={LocationVarKey.WaitlistTagEmailList} label="Custom email recipients - Waitlist Tag" />
        <InputRow {...page} keyName={LocationVarKey.ContactTagEmailList} label="Custom email recipients - Contact Tag" />
        <InputRow {...page} keyName={LocationVarKey.ServiceTagEmailList} label="Custom email recipients - Service Tag" />
        <InputRow {...page} keyName={LocationVarKey.ProblemTagEmailList} label="Custom email recipients - Problem Tag" />
        <InputRow {...page} keyName={LocationVarKey.EscalationTagEmailList} label="Custom email recipients - Escalation Tag" />
        <InputRow {...page} keyName={LocationVarKey.UnknownTagEmailList} label="Custom email recipients - Unknown Tag" />
      </Section>
    </>
  );
}
